import { Event, Campaign, CellTower } from "@mui/icons-material";
import {
  Fade,
  Card,
  CardHeader,
  CardContent,
  Typography,
  styled,
  Tooltip,
  Chip,
  Grid,
  Box,
  CardActionArea,
  Stack,
} from "@mui/material";
import { Link } from "react-router-dom";
import TransitionGroup from "react-transition-group/TransitionGroup";
import { CampaignActionDictionary, ICampaign } from "../../../App/Typings/Campaigns";
import { useCampaignActions } from "./useCampaignActions";
import {
  getCampaignTypeDisplay,
  getDisplayDuration,
} from "../../../CampaignProfile/Components/CampaignDetails";
import { getCampaignStatus } from "../../State/ManageCampaignsState";
import CRMMenu, { CrmMenuItemProps } from "../../../App/Components/Menu/CRMMenu";
import { useUserProfileStore } from "../../../App/State/Stores/UserProfileStore";

const StyledTransitionGroup = styled(TransitionGroup)({});

export const DisplayCampaignList = ({
  campaigns,
  actions,
}: {
  campaigns: ICampaign[];
  actions: CampaignActionDictionary;
}) => {
  return campaigns.length > 0 ? (
    <StyledTransitionGroup
      sx={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: {
          xs: "100%",
          sm: "repeat(2, 1fr)",
          lg: "repeat(3, 1fr)",
          xl: "repeat(4, 1fr)",
        },
        gridTemplateRows: "auto",
        gap: 2,
      }}
    >
      {campaigns.map((campaign) => (
        <Fade key={campaign.id}>
          <Card
            variant="outlined"
            data-test={"campaign-" + campaign.id}
            sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}
          >
            <DisplayCampaignCard campaign={campaign} actions={actions} />
          </Card>
        </Fade>
      ))}
    </StyledTransitionGroup>
  ) : null;
};

function DisplayCampaignCard({
  campaign,
  actions,
}: {
  campaign: ICampaign;
  actions: CampaignActionDictionary;
}) {
  const { getUserAdjustedDateFromNow, getUserAdjustedDate } = useUserProfileStore();
  const campaignStatus = getCampaignStatus(campaign);

  return (
    <CardActionArea
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
      data-test={`campaign-link-${campaign.id}`}
      component={Link}
      to={`/leadcapture/forms/${campaign.id}`}
      disableRipple={true}
    >
      <CardHeader
        title={
          <Stack direction="row" justifyContent={"flex-start"}>
            <Typography
              data-test="campaign-label"
              sx={{ fontSize: ".8em", fontWeight: "bold" }}
              noWrap
              title={campaign.name}
            >
              {campaign.name}
            </Typography>
            <Box sx={{ marginLeft: "auto", marginTop: "-5px", marginRight: "-5px" }}>
              <CampaignMenu campaign={campaign} actions={actions} />
            </Box>
          </Stack>
        }
        sx={{ width: "100%", "> .MuiCardHeader-content": { maxWidth: "100%" } }}
      />
      <CardContent
        sx={{ paddingTop: "0 !important", paddingBottom: "0 !important", width: "100%" }}
        data-test={`campaign-${campaign.id}`}
      >
        <Grid container rowSpacing={0.25}>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <CampaignChip campaign={campaign} />
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Captures:</Typography>
            <Typography fontSize={".75em"} data-test="campaign-responseCount">
              {campaign.responseCount}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Last Submission:</Typography>
            <Typography
              fontSize={".75em"}
              data-test="campaign-lastSubmissionDate"
              title={
                campaign.lastSubmissionDate
                  ? getUserAdjustedDate(campaign.lastSubmissionDate).format("MM/DD/YYYY h:mm A")
                  : ""
              }
            >
              {campaign.lastSubmissionDate ? getUserAdjustedDateFromNow(campaign.lastSubmissionDate) : ""}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Last Updated:</Typography>
            <Typography
              fontSize={".75em"}
              data-test="campaign-lastupdateddate"
              title={
                campaign.lastUpdatedDate
                  ? getUserAdjustedDate(campaign.lastUpdatedDate).format("MM/DD/YYYY h:mm A")
                  : ""
              }
            >
              {campaign.lastUpdatedDate ? getUserAdjustedDateFromNow(campaign.lastUpdatedDate) : ""}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Created:</Typography>
            <Typography
              fontSize={".75em"}
              data-test="campaign-createdDate"
              title={getUserAdjustedDate(campaign.createdDate).format("MM/DD/YYYY h:mm A")}
            >
              {getUserAdjustedDateFromNow(campaign.createdDate)}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Created By:</Typography>
            <Typography noWrap fontSize={".75em"} data-test="campaign-createdby">
              {campaign.creatorNickname}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Status:</Typography>
            <Typography fontSize={".75em"} data-test="campaign-status">
              {campaignStatus}
            </Typography>
          </Grid>
          <Grid item sx={{ mb: 2 }} xs={12} md={4}>
            <Typography sx={{ fontSize: "0.75em", fontWeight: "bold" }}>Duration:</Typography>
            <Typography fontSize={".75em"} data-test="campaign-duration">
              {getDisplayDuration(campaign)}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </CardActionArea>
  );
}

export const CampaignChip = ({ campaign }: { campaign: ICampaign }) => {
  const type = getCampaignTypeDisplay(campaign.campaignType);
  return (
    <Tooltip placement="top" title={type}>
      <Chip
        data-test={`campaign-type-${campaign.id}`}
        label={type}
        icon={CampaignIcon(type)}
        color={getChipColor(type)}
        size="small"
        sx={{ padding: "5px", maxWidth: "90%" }}
      />
    </Tooltip>
  );
};

const CampaignIcon = (type: string) => {
  switch (type) {
    case "Talent Network":
    case "Job Alerts":
    case "Job Alerts And Talent Network":
      return <CellTower data-test="tn-icon" />;
    case "Event":
      return <Event data-test="event-icon" />;
    case "Form":
    default:
      return <Campaign data-test="campaign-icon" />;
  }
};

const getChipColor = (type: string) => {
  switch (type) {
    case "Form":
      return "captivatePrimary";
    case "Event":
      return "captivateSecondary";
    default:
      return "motivatePrimary";
  }
};

export const CampaignMenu = ({
  campaign,
  actions,
}: {
  campaign: ICampaign;
  actions: CampaignActionDictionary;
}) => {
  const campaignActions = useCampaignActions(campaign, actions);

  return (
    <CRMMenu
      id={`campaign-actions-${campaign.id}`}
      links={campaignActions.map((c) => {
        return {
          id: `campaignaction-${c.id}-${campaign.id}`,
          title: c.tooltip,
          icon: c.icon,
          onClick: !c.disabled ? c.handler : undefined,
          dataTestId: `campaignaction-${c.id}-${campaign.id}`,
          tooltip: c.tooltip,
          disabled: c.disabled,
        } as CrmMenuItemProps;
      })}
      title="Form Actions"
    />
  );
};
